/**
 * Schedule indicator colors aligned with Project Dashboard `getSPIMsg`
 * (Schedule Performance Index / delay messaging).
 */
export type SpiScheduleDelayRange = {
  onTimeDelayMax: number;
  moderateDelayMax: number;
};

const GREEN = "#22C55E"; // text-green-500
const RED = "#EF4444"; // text-red-500
const AMBER = "#FBBF24"; // moderate delay

export function getSpiScheduleIndicatorHex(
  spi: number | null | undefined,
  delayRange: SpiScheduleDelayRange,
): string {
  const pct = Number(spi);
  if (!Number.isFinite(pct)) {
    return GREEN;
  }
  const diff = Math.abs(100 - pct);
  const isAhead = pct >= 100;
  if (isAhead) {
    return GREEN;
  }
  if (diff > delayRange.moderateDelayMax) {
    return RED;
  }
  if (diff > delayRange.onTimeDelayMax) {
    return AMBER;
  }
  return GREEN;
}

export type BudgetFlagThresholds = {
  onBudgetMax?: number | null;
  moderateBudgetMax?: number | null;
};

export type FlagInfo = {
  labelKey: string;
  defaultLabel: string;
  badgeClass: string;
  hexBg: string;
  hexFg: string;
};

function isStartDateInFuture(
  dates?: {
    actualStartDate?: string | Date | null;
    projectActualStartDate?: string | Date | null;
    startDate?: string | Date | null;
  } | null,
): boolean {
  if (!dates) return false;
  const rawDate =
    dates.actualStartDate ||
    dates.projectActualStartDate ||
    dates.startDate;
  if (!rawDate) return false;

  const dateObj = new Date(rawDate);
  if (isNaN(dateObj.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDay = new Date(dateObj);
  startDay.setHours(0, 0, 0, 0);

  return startDay.getTime() > today.getTime();
}

export function getScheduleFlagInfo(
  _status: string | undefined | null,
  spi: number | null | undefined,
  delayRange?: { onTimeDelayMax?: number | null; moderateDelayMax?: number | null },
  projectDates?: {
    actualStartDate?: string | Date | null;
    projectActualStartDate?: string | Date | null;
    startDate?: string | Date | null;
  } | null,
): FlagInfo {
  if (isStartDateInFuture(projectDates)) {
    return {
      labelKey: "On time",
      defaultLabel: "On time",
      badgeClass: "bg-[#22C55E]/10 text-[#22C55E]",
      hexBg: "DCFCE7",
      hexFg: "16A34A",
    };
  }

  const spiVal = Number(spi ?? 0);
  const onTimeDelayMax = delayRange?.onTimeDelayMax ?? 5;
  const moderateDelayMax = delayRange?.moderateDelayMax ?? 20;

  if (spiVal < (100 - moderateDelayMax)) {
    return {
      labelKey: "Significant Delay",
      defaultLabel: "Significant Delay",
      badgeClass: "bg-[#EF4444]/10 text-[#EF4444]",
      hexBg: "FEE2E2",
      hexFg: "DC2626",
    };
  } else if (spiVal < (100 - onTimeDelayMax)) {
    return {
      labelKey: "Moderate Delay",
      defaultLabel: "Moderate Delay",
      badgeClass: "bg-[#FAB415]/10 text-[#FAB415]",
      hexBg: "FEF9C3",
      hexFg: "B58109",
    };
  }

  return {
    labelKey: "On time",
    defaultLabel: "On time",
    badgeClass: "bg-[#22C55E]/10 text-[#22C55E]",
    hexBg: "DCFCE7",
    hexFg: "16A34A",
  };
}

export function getBudgetFlagInfo(
  _status: string | undefined | null,
  cpi: number | null | undefined,
  thresholds?: { onBudgetMax?: number | null; moderateBudgetMax?: number | null },
  projectDates?: {
    actualStartDate?: string | Date | null;
    projectActualStartDate?: string | Date | null;
    startDate?: string | Date | null;
  } | null,
): FlagInfo {
  if (isStartDateInFuture(projectDates)) {
    return {
      labelKey: "On Budget",
      defaultLabel: "On Budget",
      badgeClass: "bg-[#22C55E]/10 text-[#22C55E]",
      hexBg: "DCFCE7",
      hexFg: "16A34A",
    };
  }

  const cpiVal = Number(cpi ?? 0);
  const onBudgetMax = thresholds?.onBudgetMax ?? 5;
  const moderateBudgetMax = thresholds?.moderateBudgetMax ?? 20;

  if (cpiVal < (100 - moderateBudgetMax) && cpiVal > 0) {
    return {
      labelKey: "Significant Overrun",
      defaultLabel: "Significant Overrun",
      badgeClass: "bg-[#EF4444]/10 text-[#EF4444]",
      hexBg: "FEE2E2",
      hexFg: "DC2626",
    };
  } else if (cpiVal < (100 - onBudgetMax) && cpiVal > 0) {
    return {
      labelKey: "Moderate Overrun",
      defaultLabel: "Moderate Overrun",
      badgeClass: "bg-[#FAB415]/10 text-[#FAB415]",
      hexBg: "FEF9C3",
      hexFg: "B58109",
    };
  } else if (cpiVal > (100 + onBudgetMax)) {
    return {
      labelKey: "Under Budget",
      defaultLabel: "Under Budget",
      badgeClass: "bg-[#3B82F6]/10 text-[#3B82F6]",
      hexBg: "E0F2FE",
      hexFg: "0369A1",
    };
  }

  return {
    labelKey: "On Budget",
    defaultLabel: "On Budget",
    badgeClass: "bg-[#22C55E]/10 text-[#22C55E]",
    hexBg: "DCFCE7",
    hexFg: "16A34A",
  };
}
